import clsx from "clsx";
import { HTMLAttributes, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";

import {
  characterEnvSelectors,
  Constants,
} from "@dndbeyond/character-rules-engine";
import ChevronRight from "@dndbeyond/fontawesome-cache/svgs/solid/chevron-right.svg";

import { environment } from "~/config";
import { useCharacterEngine } from "~/hooks/useCharacterEngine";
import { useDispatch } from "~/hooks/useDispatch";
import { builderActions } from "~/tools/js/CharacterBuilder/actions";
import { NavigationUtils } from "~/tools/js/CharacterBuilder/utils";

import { BuilderMethod } from "../../constants";
import styles from "./styles.module.css";

interface Props extends HTMLAttributes<HTMLDivElement> {
  isEnabled?: boolean;
}

export const BuilderTypeChoicePage: React.FC<Props> = ({
  isEnabled = true,
  className,
  ...props
}) => {
  const { search } = useLocation();
  const isMobileApp = new URLSearchParams(search).get("platform");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { characterId } = useCharacterEngine();
  const characterIsLoading = useSelector(
    characterEnvSelectors.getLoadingStatus
  );
  // Only redirect if the character transitions from loading → loaded after mount.
  // If already loaded on mount (e.g. back from builder), do not redirect.
  const wasNotLoadedOnMount = useRef(
    characterIsLoading !== Constants.CharacterLoadingStatusEnum.LOADED
  );

  useEffect(() => {
    if (
      wasNotLoadedOnMount.current &&
      characterIsLoading === Constants.CharacterLoadingStatusEnum.LOADED
    ) {
      navigate(NavigationUtils.getCharacterBuilderUrl(characterId), {
        replace: true,
      });
    }
  }, [characterIsLoading, characterId, navigate]);

  const handleMethodSet = (method: string) => {
    if (method === BuilderMethod.STEP_BY_STEP) {
      dispatch(builderActions.stepBuildRequest(false));
    } else {
      dispatch(builderActions.builderMethodSet(method));
    }
  };

  const onKeyUp = (
    evt: React.KeyboardEvent<HTMLDivElement>,
    method: string
  ): void => {
    if (evt.key === "Enter") {
      handleMethodSet(method);
    }
  };

  const renderFooter = (text: string = "Start Building") => (
    <div className={clsx([styles.cardFooter, styles.font])}>
      <p>{text}</p>
      <ChevronRight className={styles.icon} />
    </div>
  );

  const renderCardContent = (
    title: string,
    description: string,
    badge?: string
  ) => (
    <div className={styles.cardContent}>
      <h3 className={clsx([styles.cardTitle, styles.font])}>
        {title} {badge && <span className={styles.badge}>{badge}</span>}
      </h3>
      <p className={clsx([styles.cardDescription, styles.font])}>
        {description}
      </p>
    </div>
  );

  return (
    <div className={clsx([styles.page, className])} {...props}>
      <h1 className={styles.header}>Character Creation Method</h1>
      <h2 className={clsx([styles.header])}>
        Choose how you would like to create your character
      </h2>
      <section className={styles.grid}>
        {/* STANDARD BUILD */}
        <div
          onClick={() => handleMethodSet(BuilderMethod.STEP_BY_STEP)}
          onKeyUp={(event) => onKeyUp(event, BuilderMethod.STEP_BY_STEP)}
          className={styles.card}
          role="button"
          tabIndex={0}
        >
          <div className={clsx([styles.img, styles.standardBuild])} />
          {renderCardContent(
            "Standard",
            "Create a character using a step-by-step approach"
          )}
          {renderFooter()}
        </div>
        {/* QUICK CREATE */}
        <a
          className={styles.card}
          href={
            environment === "development"
              ? "https://stg.dndbeyond.com/play/characters/build/"
              : "/play/characters/build/"
          }
        >
          <div className={clsx([styles.img, styles.quickCreate])} />
          {renderCardContent(
            "Quickbuilder",
            "A new player friendly approach to building fast and easy level one characters.",
            "New"
          )}
          {renderFooter("Start Building")}
        </a>
        {/* PREMADES */}
        {!isMobileApp && (
          <Link
            className={styles.card}
            to={`/characters/premade${search}`}
            onClick={() => handleMethodSet(BuilderMethod.PREMADES)}
          >
            <div className={clsx([styles.img, styles.premades])} />
            {renderCardContent(
              "Premade",
              "Browse a selection of ready-to-play, premade characters and claim one to your account."
            )}
            {renderFooter("Start Browsing")}
          </Link>
        )}
      </section>
    </div>
  );
};
